package com.siriusxm.pia.views.unifiedaggregator.replay

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.toCompactDurationString
import com.siriusxm.cmc.utils.toShortDateTimeString
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.*
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.backfill.backfillEventPeriods
import com.siriusxm.pia.views.unifiedaggregator.backfill.toLocaleString
import org.jetbrains.compose.web.attributes.selected
import unifiedcontent.aggregator.ReplayJobStatus
import unifiedcontent.aggregator.ReplayState
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlinx.coroutines.CancellationException
import kotlin.time.Clock
import kotlin.time.Duration
import kotlin.time.Instant

/**
 * Renders the replay list content (filters and table) without a serviceContentView wrapper.
 * Used inside the BackfillSection tab container. Displays all replay jobs within a selected
 * time range, with status icons, target info, date, and duration columns.
 */
@Composable
fun replaysContent(aggregator: AggregatorService) {
    var replays by remember { mutableStateOf<List<ReplayJobStatus>?>(null) }
    var region by remember { mutableStateOf<String?>(null) }
    var workload by remember { mutableStateOf<String?>(null) }
    var eventsPeriod by remember {
        mutableStateOf(
            TimeRange(backfillEventPeriods.first())
        )
    }

    var failureMessage by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(eventsPeriod) {
        val range = eventsPeriod.range()
        // The backend rejects some ranges (e.g. one longer than it allows) with a 400. Uncaught,
        // that crashed the page; show the message in place of the table instead, and keep the
        // header so another range can be picked (UCA-1168). An empty list stops the spinner.
        try {
            replays = aggregator.clientApi.getReplays(
                since = range.start,
                until = range.endInclusive
            ).sortedBy { it.startTime ?: Instant.DISTANT_PAST }.reversed()
            failureMessage = null
        } catch (e: CancellationException) {
            throw e
        } catch (t: Throwable) {
            replays = emptyList()
            failureMessage = t.message ?: "We were unable to load the replay jobs."
        }
    }

    if (replays == null) {
        Div({
            style {
                padding(40.px)
                property("text-align", "center")
            }
        }) {
            spinner(size = Size.LARGE, delay = 750)
        }
    } else {
        box({
            paddedContent = false
            header {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(12.px)
                    }
                }) {
                    Select({
                        title("Region")
                        onChange {
                            region = it.value?.takeIf { it != "all" }
                        }
                    }) {
                        Option("all", {
                            if (region == null) {
                                selected()
                            }
                        }) {
                            Text("All Regions")
                        }
                        listOf("us-east-2", "us-east-1", "us-west-2").forEach {
                            Option(it, {
                                if (region == it) selected()
                            }) {
                                Text(it)
                            }
                        }
                    }
                    Select({
                        title("Workload")
                        onChange {
                            workload = it.value?.takeIf { it != "all" }
                        }
                    }) {
                        Option("all", {
                            if (workload == null) selected()
                        }) { Text("All Workloads") }

                        replays?.mapNotNull { it.workload }?.distinct()?.forEach {
                            Option(it, {
                                if (workload == it) selected()
                            }) {
                                Text(it)
                            }
                        }
                    }
                    Div({ style { flex(1) } })
                    timeRangeSelect(
                        backfillEventPeriods,
                        selected = eventsPeriod
                    ) {
                        eventsPeriod = it ?: TimeRange(backfillEventPeriods.first())
                    }
                }
            }
        }) {
            failureMessage?.let { boxMessage(it) }
            if (failureMessage == null) table<ReplayJobStatus> {
                items(replays.orEmpty().filter {
                    region == null || it.region == region
                }.filter {
                    workload == null || it.workload == workload
                })

                onRowClick(hoverHighlight = true) {
                    aggregator.navigate("backfill/replays/${it.id}")
                }

                column {
                    width = 40.px
                    content {
                        replayStateIcon(it.state)
                    }
                }

                column {
                    title = "Date"
                    width = 12.em
                    content {
                        // Render in the globally-selected timezone (header picker).
                        it.startTime?.toLocalDateTimeIn(currentTimeZoneId())?.toShortDateTimeString()?.let {
                            Div {
                                Text(it)
                            }
                        }
                    }
                }

                column {
                    title = "Target"
                    width = 24.em
                    content {
                        if (it.targetArn != null) {
                            Div({
                                style {
                                    padding(4.px, 0.px)
                                }
                            }) {
                                Div({
                                    style {
                                        fontSize(0.75.em)
                                        color(SXMUI.subtleTextColor.value())
                                        lineHeight(1.2.cssRem)
                                    }
                                }) {
                                    Text(it.targetType.orEmpty())
                                }
                                Div({
                                    style {
                                        lineHeight(1.3.cssRem)
                                    }
                                }) {
                                    A(href = "#aggregator/backfill/replays/${it.id}") {
                                        Text(it.targetName.orEmpty().removePrefix("app-"))
                                    }
                                }
                            }
                        }
                    }
                }

                column {
                    title = "Event Window"
                    content {
                        // Render in the globally-selected timezone (header picker).
                        val start = it.eventStartTime?.toLocalDateTimeIn(currentTimeZoneId())?.toShortDateTimeString()
                        val end = it.eventEndTime?.toLocalDateTimeIn(currentTimeZoneId())?.toShortDateTimeString()
                        if (start != null || end != null) {
                            Text("${start ?: "?"} - ${end ?: "?"}")
                        }
                    }
                }

                column {
                    title = "Region"
                    content {
                        val targetRegion = it.targetArn?.split(":")?.get(3).orEmpty()
                            .removePrefix("us-")
                        Text(targetRegion)
                    }
                }

                column {
                    title = "Duration"
                    content {
                        Text(it.elapsedTime().toCompactDurationString())
                    }
                }
            }
        }
    }
}

/**
 * Renders a status icon for a replay state, using consistent iconography
 * with the backfill status icons.
 */
@Composable
fun replayStateIcon(state: ReplayState) {
    when (state) {
        ReplayState.COMPLETED -> {
            icon("check_circle") {
                style { color(SXMUI.goColor.value()) }
            }
        }
        ReplayState.FAILED -> {
            icon("warning") {
                style { color(Color.red) }
            }
        }
        ReplayState.CANCELLED -> {
            icon("cancel") {
                style { color(Color.gray) }
            }
        }
        ReplayState.RUNNING, ReplayState.STARTING, ReplayState.PENDING -> {
            icon("timelapse")
        }
        else -> {
            icon("help_outline") {
                style { color(Color.gray) }
            }
        }
    }
}

/** Extracts the resource name from the target ARN. */
val ReplayJobStatus.targetName: String?
    get() {
        val arnSplits = targetArn?.split(":") ?: return null
        return if (arnSplits[2] == "logs") {
            targetArn!!.substringAfter("log-group:").substringBefore(':')
        } else {
            targetArn!!.substringAfterLast("/")
        }
    }

/** Extracts a human-readable target type from the target ARN service component. */
val ReplayJobStatus.targetType: String?
    get() {
        val arnSplits = targetArn?.split(":") ?: return null
        return when (val svc = arnSplits[2]) {
            "events" -> "Event Bus"
            "logs" -> "Log Group"
            "kinesis" -> "Kinesis Stream"
            "lambda" -> "Lambda"
            "sqs" -> "SQS Queue"
            else -> svc
        }
    }

/** Extracts the AWS region from the target ARN. */
val ReplayJobStatus.region: String?
    get() {
        return targetArn?.split(":")?.get(3)
    }

/** Extracts the workload name from the target resource name (e.g. "app-{workload}-..."). */
val ReplayJobStatus.workload: String?
    get() {
        return targetName?.let {
            if (it.startsWith("app-")) {
                it.removePrefix("app-").split("-").let {
                    "${it.first()}-${it.get(1)}"
                }
            } else null
        }
    }

/** Calculates elapsed time from start to end (or now if still running). */
fun ReplayJobStatus.elapsedTime(): Duration {
    val now = Clock.System.now()
    return (endTime ?: now) - (startTime ?: now)
}

/** Returns true if the replay is still actively running. */
fun ReplayJobStatus.isRunning(): Boolean {
    return state == ReplayState.RUNNING || state == ReplayState.STARTING || state == ReplayState.PENDING
}
